import { Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { usePermissions } from '../hooks/usePermissions';

/**
 * RequirePermission - Route-level permission guard component.
 *
 * ==================================================================================
 * WHEN TO USE THIS COMPONENT:
 * ==================================================================================
 * Use this component for ROUTE-LEVEL protection:
 * - Wrapping entire routes/pages in App.js
 * - Protecting access to complete features/sections
 * - When you need to redirect unauthorized users
 * - When the entire page should be inaccessible without permission
 *
 * Examples:
 *    Protect /settings route (redirect users without business permissions)
 *    Protect /admin-panel route (redirect users without ADMIN permission)
 *    Protect project settings page (redirect users without PROJECT_WRITE)
 *    Wrap entire page components that require specific permissions
 *
 * ==================================================================================
 * WHEN NOT TO USE THIS COMPONENT:
 * ==================================================================================
 *  DON'T use for hiding individual buttons/elements - use usePermissions hook instead
 *  DON'T use for conditional rendering within a component - use usePermissions hook instead
 *  DON'T use for nav items in sidebar - use usePermissions hook instead
 *
 * For component-level conditional rendering, see: src/hooks/usePermissions.js
 * ==================================================================================
 *
 * Protects routes based on business or project permissions.
 * Shows loading spinner while permissions are being fetched.
 * Redirects to fallback if user lacks permission.
 *
 * @param {Object} props
 * @param {string} props.permission - Single permission to check
 * @param {string[]} props.permissions - Array of permissions to check
 * @param {'any' | 'all'} props.mode - When checking multiple permissions: 'any' = user needs at least one, 'all' = user needs all (default: 'any')
 * @param {'business' | 'project'} props.scope - Permission scope (default: 'business')
 * @param {string} props.projectId - Required when scope is 'project'
 * @param {React.ReactNode} props.fallback - What to render when access denied (default: redirect to /projects)
 * @param {React.ReactNode} props.children - Content to render when access granted
 *
 * @example
 * // Protect /settings route - user needs ANY business permission
 * <Route
 *     path="/settings"
 *     element={
 *         <RequirePermission permissions={ANY_BUSINESS_PERMISSION}>
 *             <GlobalSettings />
 *         </RequirePermission>
 *     }
 * />
 *
 * @example
 * // Protect admin panel - user needs ADMIN permission
 * <Route
 *     path="/admin"
 *     element={
 *         <RequirePermission permission="ADMIN">
 *             <AdminPanel />
 *         </RequirePermission>
 *     }
 * />
 *
 * @example
 * // Protect project settings - user needs ALL of multiple permissions
 * <Route
 *     path="/projects/:id/advanced-settings"
 *     element={
 *         <RequirePermission
 *             permissions={['PROJECT_WRITE', 'ADMIN']}
 *             mode="all"
 *         >
 *             <AdvancedSettings />
 *         </RequirePermission>
 *     }
 * />
 *
 * @example
 * // Project-scoped permission with custom fallback
 * <RequirePermission
 *     permission="DEFICIENCY_WRITE"
 *     scope="project"
 *     projectId={projectId}
 *     fallback={<Navigate to="/projects" />}
 * >
 *     <DeficiencyEditor />
 * </RequirePermission>
 */
function RequirePermission({
    permission,
    permissions,
    mode = 'any',
    scope = 'business',
    projectId,
    fallback = <Navigate to="/projects" />,
    children,
}) {
    const {
        canBusiness,
        canAnyBusiness,
        canAllBusiness,
        canProject,
        canAnyProject,
        canAllProject,
        isLoading,
    } = usePermissions();

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    let hasPermission;
    if (scope === 'business') {
        if (permissions) {
            hasPermission =
                mode === 'all' ? canAllBusiness(permissions) : canAnyBusiness(permissions);
        } else {
            hasPermission = canBusiness(permission);
        }
    } else {
        if (permissions) {
            hasPermission =
                mode === 'all'
                    ? canAllProject(projectId, permissions)
                    : canAnyProject(projectId, permissions);
        } else {
            hasPermission = canProject(projectId, permission);
        }
    }

    return hasPermission ? children : fallback;
}

export default RequirePermission;
